import React, { useState, useCallback } from "react";
import AreaSidebar from "./components/AreaSidebar";
import AreaEditor from "./components/AreaEditor";
import { useAreas } from "./hooks/useAreas";
import { areaDefaults } from "../../../models/area";
import { ConfirmDialog, MasterDetailLayout, Breadcrumb } from "../../../components/Common";

/**
 * Areas Page (Módulo de Gerenciamento de Espaços e Áreas)
 * Layout Master-Detail: Listagem lateral e editor principal.
 */
const AreasPage = () => {
    const { areas, loading, isSaving, actions } = useAreas();
    const [selectedArea, setSelectedArea] = useState(null);
    const [isDirty, setIsDirty] = useState(false);

    // Seleção inicial automática
    React.useEffect(() => {
        if (!selectedArea && areas.length > 0 && !loading) {
            setSelectedArea(areas[0]);
        }
    }, [areas, loading, selectedArea]);

    const handleSelectArea = useCallback(async (area) => {
        if (selectedArea?.id === area.id) return;

        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Você tem alterações pendentes nesta área. Deseja descartá-las?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }

        setSelectedArea(area);
        setIsDirty(false);
    }, [selectedArea, isDirty]);

    const handleCreateNew = useCallback(async () => {
        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais e criar uma nova área?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }
        setSelectedArea(areaDefaults);
        setIsDirty(false);
    }, [isDirty]);

    const handleSave = useCallback(async (data) => {
        const success = await actions.saveArea(data);
        if (success) {
            setIsDirty(false);
        }
    }, [actions]);

    const handleDelete = useCallback(async (id) => {
        const success = await actions.deleteArea(id);
        if (success) {
            setSelectedArea(null);
            setIsDirty(false);
        }
    }, [actions]);

    const handleDirtyChange = useCallback((dirty) => setIsDirty(dirty), []);

    // PageLoader logic removido para carregamento nativo do sidebar/detalhe

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Espaços e Áreas" />
            </div>

            <div className="border-top mb-3"></div>

            <div className="areas-container mt-3">
                <MasterDetailLayout
                master={
                    <AreaSidebar
                        areas={areas}
                        activeId={selectedArea?.id}
                        onSelect={handleSelectArea}
                        onCreateNew={handleCreateNew}
                        loading={loading}
                    />
                }
                detail={
                    <AreaEditor
                        area={selectedArea}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        isSaving={isSaving}
                        onChange={handleDirtyChange}
                    />
                }
            />
            </div>
        </React.Fragment>
    );
};

export default AreasPage;
